import type { ResolvedNode } from '../../../../vendor/agentplace-a2ui/walker.ts';
import { stripProviderCitationMarkers } from '../../../../../shared/provider-citations.ts';
import { isRecord } from '../util/type-guards.ts';

const OPAQUE_PROPS = new Set(['action', 'checks', 'context', 'code', 'url', 'href', 'src', 'path']);

function isOpaqueProp(key: string): boolean {
  return (
    OPAQUE_PROPS.has(key) ||
    key === 'id' ||
    key.endsWith('Id') ||
    key.endsWith('Ids') ||
    key.endsWith('Url') ||
    key.endsWith('URL') ||
    key.endsWith('Uri')
  );
}

function displayValue(value: unknown): unknown {
  if (typeof value === 'string') {
    return stripProviderCitationMarkers(value);
  }
  if (Array.isArray(value)) {
    const items = value.map(displayValue);
    return items.some((item, index) => item !== value[index]) ? items : value;
  }
  if (isRecord(value)) {
    return displayProps(value);
  }
  return value;
}

function displayProps(
  props: Record<string, unknown>,
  bindings: Record<string, string> = {},
): Record<string, unknown> {
  let result = props;
  for (const [key, value] of Object.entries(props)) {
    if (isOpaqueProp(key) || ((key === 'value' || key === 'values') && key in bindings)) {
      continue;
    }
    const displayed = displayValue(value);
    if (displayed !== value) {
      if (result === props) {
        result = { ...props };
      }
      result[key] = displayed;
    }
  }
  return result;
}

/**
 * Project a resolved node for display in any registered component, including replay.
 * Preserve the stored graph, identity, actions, bindings and controlled field values.
 * Children are projected independently by the renderer as it walks the tree.
 */
export function surfaceNodeForDisplay(node: ResolvedNode): ResolvedNode {
  const props = displayProps(node.props, node.bindings);
  return props === node.props ? node : { ...node, props };
}
